import React, { useEffect, useState } from "react";
import { Col } from "reactstrap";
import { getFormattedContactNumber, Toast } from "utility/helpers";
import { useNavigate, useLocation } from "react-router";
import { genericGet, genericPost, genericPut } from "services/services.general";
import { fetchAllRegions } from "services/services.congressman";
import {
  fetchProvinces,
  fetchMunicipalities,
} from "services/services.projects";
import styled from "styled-components";
import { HeaderText } from "assets/js/Styles";
import { NCR_REGION_ID } from "utility/constants";
import { getHeaderText, validateTVISettingsForm } from "./utility";
import TVISettingsFormSection from "./TVISettingsFormSection";

const HeaderTextSection = styled.div`
  margin-top: 24px;
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 4rem;
  }
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const AddTviForm = () => {
  const location = useLocation();

  const navigate = useNavigate();
  const row = location.state?.row || null;
  const { action } = location.state;

  const [selectedCourses, setSelectedCourses] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [coursesOptions, setCoursesOptions] = useState([]);

  const [form, setForm] = useState({
    name: null,
    provinceId: 0,
    regionId: 0,
    municipalityId: 0,
    contactNumber: "",
    courses: [],
  });

  const fetchRegions = () => {
    fetchAllRegions({
      callback: (data) => {
        const options = data
          .filter((region) => region.name !== "Partylist")
          .map(({ acronym, id, omegaId }) => ({
            omegaId,
            label: acronym,
            value: id,
          }));

        setRegionOptions(options);

        const region = options?.find(
          (option) => option.value === row?.regionId
        );

        if (region) {
          setForm({
            ...form,
            regionId: region,
          });
        }
        setIsLoading(false);
      },
    });
  };

  const fetchAllProvinces = () => {
    fetchProvinces({
      param: form?.regionId?.value,
      callback: (data) => {
        if (data.length) {
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          setProvinceOptions(options);

          setForm((prevForm) => ({
            ...prevForm,
            provinceId: options[0],
            municipalityId: null,
          }));

          const option = options?.find((op) => op.value === row?.provinceId);

          if (option) {
            setForm({
              ...form,
              provinceId: option,
            });
          }
        } else {
          setProvinceOptions([]);
        }
      },
    });
  };

  const fetchAllMunicipalities = () => {
    fetchMunicipalities({
      param: form?.provinceId?.value,
      callback: (data) => {
        if (data?.length) {
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          setMunicipalityOptions(options);

          const option = options?.find(
            (op) => op.value === row?.municipalityId
          );

          if (form?.regionId?.value === NCR_REGION_ID) {
            options.unshift({
              label: "All Municipalities",
              value: "All Municipalities",
            });

            setForm({
              ...form,
              municipalityId: options[0],
            });
          }

          if (option) {
            setForm({
              ...form,
              municipalityId: option,
            });
          }
        } else {
          setMunicipalityOptions([]);
        }
      },
    });
  };

  const fetchCourses = () => {
    genericGet({
      url: `tvi/province/${form?.provinceId?.value}/courses`,
      callback: (data) => {
        const selectedMap = new Map(
          selectedCourses.map((course) => [course.courseId, course])
        );

        const options = data.rows.map(
          ({ id, courseId, course, sector, availableSlots }) => {
            const selected = selectedMap.get(courseId);

            return {
              courseCapacityId: id,
              courseId,
              course,
              sector,
              availableSlots,
              isFull: false,
              hasEndorsements: selected ? selected.hasEndorsements : false,
            };
          }
        );

        const mergedOptions = [
          ...options,
          ...selectedCourses
            .filter(
              (defaultCourse) =>
                !options.some(
                  (option) => option.courseId === defaultCourse.courseId
                )
            )
            .map(
              ({
                id,
                courseId,
                course,
                sector,
                availableSlots,
                hasEndorsements,
              }) => ({
                courseCapacityId: id,
                courseId,
                course,
                sector,
                availableSlots,
                isFull: true,
                hasEndorsements,
              })
            ),
        ];

        setCoursesOptions(mergedOptions);
        setIsLoading(false);
      },
    });
  };

  const saveData = (formData) => {
    genericPost({
      url: "/tvi",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          navigate("/settings/tesda/tvi");

          Toast({
            type: 1,
            content: "Successfully created course.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const updateData = (formData, id) => {
    genericPut({
      url: `/tvi/${id}`,
      formData,
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated course.",
            });
          }, 1500);

          navigate("/settings/tesda/tvi");
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const handleSubmit = (formValues) => {
    const coursesValidation = validateTVISettingsForm({
      form: formValues,
      selectedCourses,
      coursesOptions,
      action,
    });

    if (typeof coursesValidation !== "boolean") {
      Toast({
        type: 4,
        content: coursesValidation,
      });

      return;
    }

    setIsLoading(true);

    const formData = {
      name: formValues.name,
      regionId: formValues.regionId?.value,
      provinceId: formValues.provinceId?.value,
      ...(formValues?.municipalityId?.value !== "All Municipalities"
        ? { municipalityId: formValues.municipalityId?.value ?? null }
        : {}),
      contactNumber:
        formValues.contactNumber === "" ? null : formValues.contactNumber,
      courses: selectedCourses.map(
        // eslint-disable-next-line no-unused-vars
        ({ oldCapacityValue: _, ...rest }) => rest
      ),
    };

    if (row === null) {
      saveData(formData);
    } else {
      updateData(formData, row.id);
    }
  };

  const setDefaultSelectedCourses = () => {
    return new Promise((resolve, reject) => {
      genericGet({
        url: `/tvi/${row.id}`,
        callback: (data) => {
          const selectedDefaultCourses = data?.courses
            .filter((course) => course.id !== null)
            .map(
              ({
                id,
                courseId,
                capacity,
                availableSlots,
                course,
                sector,
                hasEndorsements,
              }) => ({
                id,
                courseId,
                capacity,
                oldCapacityValue: capacity,
                availableSlots,
                course,
                sector,
                hasEndorsements,
              })
            );

          setSelectedCourses(selectedDefaultCourses);

          resolve();
        },
        error: (err) => reject(err),
      });
    });
  };

  const setFormData = () => {
    setForm({
      name: row.name,
      provinceId: row.provinceId,
      regionId: row.regionId,
      municipalityId: row.municipalityId,
      contactNumber: getFormattedContactNumber(row.contactNumber) || null,
      courses: selectedCourses,
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchRegions();
  }, []);

  useEffect(() => {
    if (row) {
      setFormData();
      setDefaultSelectedCourses();
    }
  }, [row]);

  useEffect(() => {
    if (form?.regionId) {
      fetchAllProvinces();
    }
  }, [form?.regionId]);

  useEffect(() => {
    if (form?.provinceId) {
      fetchAllMunicipalities();
      fetchCourses();

      setForm({
        ...form,
        municipalityId: null,
      });
    }
  }, [form?.provinceId]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getHeaderText(action)}</HeaderText>
        </HeaderTextSection>

        <TVISettingsFormSection
          form={form}
          setForm={setForm}
          action={action}
          isLoading={isLoading}
          regionOptions={regionOptions}
          provinceOptions={provinceOptions}
          municipalityOptions={municipalityOptions}
          coursesOptions={coursesOptions}
          setSelectedCourses={setSelectedCourses}
          selectedCourses={selectedCourses}
          handleSubmit={handleSubmit}
        />
      </Col>
    </Wrapper>
  );
};

export default AddTviForm;
